import React, { useState } from "react";
import { Loader2, Send, RotateCw, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { evaluatePrompt } from "../lib/api";
import { useProgress } from "../context/ProgressContext";
import { motion, AnimatePresence } from "framer-motion";

export function PromptPlayground({ lesson, moduleId, onScoreEarned }) {
  const { sessionId } = useProgress();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!text.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await evaluatePrompt({
        session_id: sessionId,
        module_id: moduleId,
        lesson_id: lesson.id,
        scenario: lesson.scenario,
        rubric: lesson.rubric || [],
        user_prompt: text,
      });
      setResult(res);
      if (onScoreEarned) onScoreEarned(res);
    } catch (e) {
      console.error(e);
      setError("Evaluation service unavailable. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setText(""); setResult(null); setError(null); };

  return (
    <div className="space-y-6">
      <div className="relative">
        <div className="flex items-center justify-between mb-2 font-mono-code text-[10px] uppercase tracking-widest text-muted-foreground">
          <span>prompt.editor</span>
          <span>{text.length} chars</span>
        </div>
        <textarea
          data-testid="prompt-editor-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={9}
          placeholder="Write your prompt here...&#10;&#10;Try: 'You are a...' followed by a clear task, context, and format."
          className="w-full font-mono-code text-sm bg-surface border border-border p-4 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-y"
          disabled={loading}
        />
      </div>

      {lesson.hint && !result && (
        <div className="text-xs font-mono-code text-muted-foreground border-l-2 border-primary/40 pl-3 py-1">
          <span className="uppercase tracking-widest text-[10px]">hint · </span>
          {lesson.hint}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          data-testid="prompt-evaluate-btn"
          onClick={submit}
          disabled={loading || !text.trim()}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 font-medium border-2 border-primary hard-shadow-ink disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {loading ? "Evaluating…" : "Evaluate Prompt"}
        </button>
        {(text || result) && (
          <button
            data-testid="prompt-reset-btn"
            onClick={reset}
            className="inline-flex items-center gap-2 border-2 border-border px-4 py-2.5 hover:border-primary transition-colors"
          >
            <RotateCw className="w-4 h-4" /> Reset
          </button>
        )}
      </div>

      {error && (
        <div className="border border-destructive/50 bg-destructive/5 text-destructive text-sm p-4" data-testid="eval-error">
          {error}
        </div>
      )}

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="border-2 border-primary bg-surface p-6 md:p-8 relative overflow-hidden grain"
            data-testid="eval-result"
          >
            <div className="flex items-start justify-between gap-6 mb-6">
              <div>
                <div className="font-mono-code text-[10px] uppercase tracking-widest text-muted-foreground">
                  ai coach · verdict
                </div>
                <div className="font-serif-display text-4xl md:text-5xl mt-1" data-testid="eval-verdict">
                  {result.verdict}
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono-code text-[10px] uppercase tracking-widest text-muted-foreground">score</div>
                <div className="font-serif-display text-5xl md:text-6xl leading-none" data-testid="eval-score">
                  {result.score}
                </div>
                <div className="font-mono-code text-xs text-primary mt-1">+{result.xp_awarded} XP</div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <div className="font-mono-code text-[10px] uppercase tracking-widest text-success mb-2 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> strengths
                </div>
                <ul className="space-y-1.5 text-sm">
                  {result.strengths?.map((s, i) => (
                    <li key={i} className="flex gap-2"><span className="text-success">·</span>{s}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="font-mono-code text-[10px] uppercase tracking-widest text-primary mb-2 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" /> improvements
                </div>
                <ul className="space-y-1.5 text-sm">
                  {result.improvements?.map((s, i) => (
                    <li key={i} className="flex gap-2"><span className="text-primary">·</span>{s}</li>
                  ))}
                </ul>
              </div>
            </div>

            {result.example_prompt && (
              <div>
                <div className="font-mono-code text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> improved rewrite
                </div>
                <div className="font-mono-code text-sm bg-background border border-border p-4 leading-relaxed">
                  {result.example_prompt}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
